import { logActivity } from "./activity-log";
import { moduleFromPathname } from "./modulo-de-ruta";

/**
 * Tiempos reales de carga de la app en el aparato de cada usuario (#1717 R13).
 *
 * Por qué existe: el 16-oct-2026 la lentitud se supo porque un médico de Venezuela la reclamó.
 * Sin esto no había cómo saber quién carga lento ni desde qué conexión, y los leads que se
 * cansan de esperar en el registro se van sin decir nada.
 *
 * Qué manda (evento `carga_app`, sin PHI; el backend solo acepta estas claves y con estos tipos,
 * ver `finetuning/activity.py` `_EVENT_SCOPED_META`): TTFB, FCP y LCP en ms, KB de JS bajados de
 * la red (0 = vino del caché), tipo de conexión, si fue en el celular y si fue antes de ingresar.
 *
 * El lead que todavía no se registra no tiene sesión, y `logActivity` descarta lo que no tiene
 * dueño. Entonces la medición se guarda en el navegador y se manda cuando ingresa, marcada
 * `antes_de_ingresar`: es justo la carga que más importa (la del link de registro).
 * Cubre R13 — .claude/specs/1717-velocidad/
 */
export const CLAVE_PENDIENTE = "smartdx.carga_app_pendiente.v1";
const VIGENCIA_MS = 24 * 60 * 60 * 1000;
const ESPERA_MS = 15000;
const CONEXIONES = new Set(["slow-2g", "2g", "3g", "4g"]);

const entero = (v) => (Number.isFinite(v) && v >= 0 ? Math.round(v) : undefined);

export function tieneSesion(storage = safeStorage()) {
  try {
    return Boolean(storage && storage.getItem("authToken"));
  } catch (_) {
    return false;
  }
}

function safeStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (_) {
    return null;
  }
}

// El pendiente del lead vive en sessionStorage, no en localStorage: solo sobrevive en la MISMA
// pestaña (el lead que abre el link y se registra ahí). En un computador compartido, la carga de un
// lead que se fue no se le atribuye al próximo que ingresa (O10 del revisor v2).
function safeSession() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch (_) {
    return null;
  }
}

export function moduloDeRuta(pathname) {
  return moduleFromPathname(pathname);
}

export function armarMedicion({ vitals = {}, recursos = [], conexion, ancho, ua = "", antesDeIngresar }) {
  const bytesJs = recursos
    .filter((r) => /\/static\/js\/[^/]+\.js$/.test(String(r.name || "")))
    .reduce((acc, r) => acc + (Number(r.transferSize) || 0), 0);
  const meta = {
    ttfb_ms: entero(vitals.ttfb),
    fcp_ms: entero(vitals.fcp),
    lcp_ms: entero(vitals.lcp),
    js_kb: Math.round(bytesJs / 1024),
    conexion: CONEXIONES.has(conexion) ? conexion : "desconocida",
    movil: (Number(ancho) > 0 && Number(ancho) <= 768) || /Mobi|Android|iPhone|iPad/i.test(ua),
    antes_de_ingresar: Boolean(antesDeIngresar),
  };
  Object.keys(meta).forEach((k) => meta[k] === undefined && delete meta[k]);
  return meta;
}

export function enviarOGuardar(meta, module, { storage = safeStorage(), pendientes = safeSession(), log = logActivity, ahora = Date.now() } = {}) {
  if (tieneSesion(storage)) {
    log("carga_app", { module, meta });
    return "enviada";
  }
  try {
    pendientes?.setItem(CLAVE_PENDIENTE, JSON.stringify({ meta, module, at: ahora }));
  } catch (_) {
    /* sin storage: se pierde esta medición, nada más */
  }
  return "guardada";
}

export function enviarPendiente({ storage = safeStorage(), pendientes = safeSession(), log = logActivity, ahora = Date.now() } = {}) {
  try {
    if (!pendientes || !tieneSesion(storage)) return false;
    const raw = pendientes.getItem(CLAVE_PENDIENTE);
    if (!raw) return false;
    pendientes.removeItem(CLAVE_PENDIENTE);
    const dato = JSON.parse(raw);
    if (!dato || !dato.meta || ahora - Number(dato.at || 0) > VIGENCIA_MS) return false;
    log("carga_app", { module: dato.module, meta: { ...dato.meta, antes_de_ingresar: true } });
    return true;
  } catch (_) {
    return false;
  }
}

/** Se llama una vez al arrancar la app (index.js). Nunca lanza. */
export function instalarMedicionCarga() {
  try {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    enviarPendiente();
    const alVolver = () => enviarPendiente();
    window.addEventListener("focus", alVolver);

    const antesDeIngresar = !tieneSesion();
    const module = moduloDeRuta(window.location.pathname);
    const vitals = {};
    let enviada = false;
    const enviar = () => {
      if (enviada) return;
      enviada = true;
      let recursos = [];
      try {
        recursos = window.performance?.getEntriesByType?.("resource") || [];
      } catch (_) {}
      const meta = armarMedicion({
        vitals,
        recursos,
        conexion: navigator?.connection?.effectiveType,
        ancho: window.innerWidth,
        ua: navigator?.userAgent || "",
        antesDeIngresar,
      });
      enviarOGuardar(meta, module);
    };
    import("web-vitals")
      .then(({ getFCP, getLCP, getTTFB }) => {
        getTTFB((m) => { vitals.ttfb = m.value; });
        getFCP((m) => { vitals.fcp = m.value; });
        getLCP((m) => { vitals.lcp = m.value; }, true); // true: cada cambio, así hay LCP a los 15 s sin input (O9)
      })
      .catch(() => {});
    setTimeout(enviar, ESPERA_MS);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") enviar();
    });
  } catch (_) {
    /* telemetría: nunca rompe el arranque */
  }
}
